import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportProductDetails from "hooks/organization/reports/types/useGetReportProductDetails";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableProductDetails(startDate, endDate) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportProductDetails, { data, loading, error }] = useGetReportProductDetails(false); // No success message for filtering/pagination

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        page,
        first,
      };
      getReportProductDetails({ variables });
    },
    [startDate, endDate, getReportProductDetails]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportProductDetails?.paginatorInfo,
  });

  // Format currency for display
  const formatCurrency = (amount: number) => {
    if (amount == null) return "Unavailable";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const columns = useMemo(() => {
    return [
      { Header: "Cost basis", accessor: "costBasis" },
      { Header: "Items without costs", accessor: "itemsWithoutCosts" },
      {
        Header: "Product ID",
        accessor: "productId",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Product Name",
        accessor: "productName",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Product Type",
        accessor: "productType",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Material Cost Type",
        accessor: "materialCostType",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Material Needed Sum",
        accessor: "materialNeededSum",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposal Count",
        accessor: "proposalsCount",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposal Expenses Sum",
        accessor: "sumCostTotal",
        Cell: ({ value }) => formatCurrency(value) ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposal Profit Sum",
        accessor: "sumProfit",
        Cell: ({ value }) => formatCurrency(value) ?? "",
        disableSortBy: true,
      },
      {
        Header: "Proposal Amount Sum",
        accessor: "sumOverallTotal",
        Cell: ({ value }) => formatCurrency(value) ?? "",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportProductDetails?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportProductDetails: paginate,
    metadata: data?.getReportProductDetails?.metadata,
  } as const;
}
